import { isEmpty } from 'lodash';
import { Session } from 'next-auth';
import { useActiveFeatureFlags } from 'posthog-js/react';
import { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { APP_NAV_ITEMS, APP_NAV_SIZES, LOCAL_STORAGE_APP_NAV } from '@/lib/constants';
import { appNavState } from '@/state/navigation';
import { BREAKPOINTS_AS_NUMBERS } from '@/theme/constants/breakpoints';
import { getStoredItem } from '@/utils/store/local';

import { useEffectOnlyRunOnFirstMount } from './use-effect-only-on-first-mount';
import { useWindowSize } from './use-window-size';

const setAppNavWidthCssVar = (isOpen: boolean, isSmallScreen: boolean) => {
  let width;
  if (isOpen) {
    width = isSmallScreen ? APP_NAV_SIZES.openMobile : APP_NAV_SIZES.open;
  } else {
    width = APP_NAV_SIZES.closed;
  }
  document.body.style.setProperty('--app-nav-width', width);
};

interface Props {
  session?: Session | null;
  showAllItemsOverride?: boolean; // Only used in storybook and tests
}

export const useAppNavigation = ({ session, showAllItemsOverride }: Props) => {
  const { width } = useWindowSize();
  const [appNav, setAppNav] = useRecoilState(appNavState);
  const { isOpen } = appNav;
  const isSmallScreen = width! < BREAKPOINTS_AS_NUMBERS.MD;
  const isFirstRender = useEffectOnlyRunOnFirstMount();
  const activeFeatureFlags = useActiveFeatureFlags();

  const entitlements = session?.entitlements || {};

  useEffect(() => {
    if (isFirstRender && getStoredItem(LOCAL_STORAGE_APP_NAV)) {
      const appStateFromLocalStorage = getStoredItem(LOCAL_STORAGE_APP_NAV);
      setAppNav(appStateFromLocalStorage);
    }
  }, []);

  useEffect(() => {
    if (isSmallScreen && !isOpen) {
      setAppNav({ ...appNav, isOpen: true });
    }
  }, [isSmallScreen, isOpen]);

  useEffect(() => {
    setAppNavWidthCssVar(isOpen, isSmallScreen);
  }, [isOpen, isSmallScreen]);

  const navItems = () => {
    // First filter out sub items
    const filteredSubNavItems = APP_NAV_ITEMS.map((item) => {
      const filteredSubItems = item.subItems.filter((subItem) => {
        if (showAllItemsOverride) return true;

        return !subItem.entitlement || entitlements[subItem.entitlement];
      });
      return {
        ...item,
        subItems: filteredSubItems,
      };
    });

    const filtered = filteredSubNavItems.filter((item) => {
      if (showAllItemsOverride) return true;

      // Any items that don't have the entitlement or
      // featureFlag keys are available to all users
      const itemPassesFeatureFlagCheck = !item.featureFlag || activeFeatureFlags?.includes(item.featureFlag);
      const itemPassesEntitlementCheck = !item.entitlement || entitlements[item.entitlement];
      const hasSubItems = !isEmpty(item.subItems);

      return (itemPassesFeatureFlagCheck && itemPassesEntitlementCheck && hasSubItems) || false;
    });

    return filtered;
  };

  return { navItems };
};
